import type {
  ConfirmAppointmentRow,
  SendConfirmationMailStepOutput,
} from "../confirm-appointments-contract"
import {
  dateFromBatchValues,
  dateMatchesBatchRange,
  textMatchesBatchSearch,
  type BatchDateRangeFilter,
} from "../../../../../../../../_shared/_logic/filter-values"

export type ConfirmAppointmentFilterState = {
  search: string
  status: string
  employee: string
  dateRange: BatchDateRangeFilter
}

export type ConfirmationMailFilterState = {
  search: string
  mailStatus: string
  dateRange: BatchDateRangeFilter
}

export const createConfirmAppointmentFilters = (
  overrides: Partial<ConfirmAppointmentFilterState> = {},
): ConfirmAppointmentFilterState => ({
  search: "",
  status: "",
  employee: "",
  dateRange: { start: "", end: "" },
  ...overrides,
})

export const createConfirmationMailFilters = (
  overrides: Partial<ConfirmationMailFilterState> = {},
): ConfirmationMailFilterState => ({
  search: "",
  mailStatus: "",
  dateRange: { start: "", end: "" },
  ...overrides,
})

export const confirmAppointmentEmployeeLabel = (row: ConfirmAppointmentRow) =>
  row.workOrderRow?.currentAppointmentEmployeeLabel ||
  (row.event as { employee?: { display_name?: string | null } }).employee?.display_name ||
  row.event.employee_name ||
  ""

export const confirmAppointmentStatusLabel = (row: ConfirmAppointmentRow) =>
  String(row.event.status ?? "").trim() || "unbekannt"

const rowDate = (row: ConfirmAppointmentRow) =>
  dateFromBatchValues(
    row.event.start_at,
    (row.event as { slot_start_at?: string | null }).slot_start_at,
  )

const rowSearchValues = (row: ConfirmAppointmentRow) => [
  row.workOrder?.order_number,
  row.workOrderRow?.orderSummaryLabel,
  row.workOrderRow?.orderSummarySubLabel,
  row.workOrderRow?.propertyAddressLines?.join(" "),
  row.event.title,
  row.event.location_text,
  confirmAppointmentEmployeeLabel(row),
  confirmAppointmentStatusLabel(row),
]

export const filterConfirmAppointmentRows = (
  rows: ConfirmAppointmentRow[],
  filters: ConfirmAppointmentFilterState,
) =>
  rows.filter(
    (row) =>
      (!filters.status || confirmAppointmentStatusLabel(row) === filters.status) &&
      (!filters.employee || confirmAppointmentEmployeeLabel(row) === filters.employee) &&
      dateMatchesBatchRange(rowDate(row), filters.dateRange) &&
      textMatchesBatchSearch(filters.search, rowSearchValues(row)),
  )

export const mailStatusForRow = (
  row: ConfirmAppointmentRow,
  output: SendConfirmationMailStepOutput | null | undefined,
) => output?.items.find((item) => item.event_id === row.event.id)?.status ?? "bereit"

export const filterConfirmationMailRows = (
  rows: ConfirmAppointmentRow[],
  filters: ConfirmationMailFilterState,
  output: SendConfirmationMailStepOutput | null | undefined,
) =>
  rows.filter(
    (row) =>
      (!filters.mailStatus || mailStatusForRow(row, output) === filters.mailStatus) &&
      dateMatchesBatchRange(rowDate(row), filters.dateRange) &&
      textMatchesBatchSearch(filters.search, [
        ...rowSearchValues(row),
        mailStatusForRow(row, output),
      ]),
  )
